<template>
  <div v-if="invoice.can_convert_to_closing_invoice || invoice.closing_invoice_data" class="w-100">
    <template v-if="invoice.can_convert_to_closing_invoice">
      <button type="button" class="btn btn-outline-primary" :disabled="disabled || busy" @click="open">
        {{ $t('invoices.closing.convert') }}
      </button>
      <span v-if="disabled" class="ms-2 text-muted">{{ $t('invoices.closing.save_first') }}</span>
    </template>
    <div v-if="invoice.closing_invoice_data" class="alert alert-info mb-0">
      <strong>{{ $t('invoices.closing.title') }}</strong>
      <div v-for="partial in invoice.closing_invoice_data.partial_invoices" :key="partial.id">
        {{ $t('invoices.closing.less') }} {{ partial.number }}:
        {{ formatCurrency(partial.gross_total) }}
      </div>
      <strong>{{ $t('invoices.balance_due') }}: {{ formatCurrency(invoice.closing_invoice_data.balance_due) }}</strong>
    </div>
    <Dialog v-model:visible="visible" :modal="true" :header="$t('invoices.closing.convert')"
            :style="{width: '55rem', maxWidth: '95vw'}" :closable="!busy" :close-on-escape="!busy">
      <p>{{ $t('invoices.closing.draft_only') }}</p>
      <p v-if="error" role="alert" class="alert alert-danger">{{ error }}</p>
      <p v-if="busy" role="status">{{ $t('invoices.closing.loading') }}</p>
      <template v-if="options">
        <fieldset :disabled="busy">
          <legend class="fs-5">{{ $t('invoices.closing.partials') }}</legend>
          <p v-if="!options.partial_invoices.length">{{ $t('invoices.closing.no_partials') }}</p>
          <div v-for="partial in options.partial_invoices" :key="partial.id" class="form-check mb-2">
            <input :id="`closing-partial-${partial.id}`" v-model="partialInvoiceIds" :value="partial.id"
                   type="checkbox" class="form-check-input" @change="invalidate">
            <label :for="`closing-partial-${partial.id}`" class="form-check-label">
              {{ partial.number }} {{ $t('invoices.closing.dated') }} {{ formatDate(partial.invoice_date, 'short2') }}:
              {{ formatCurrency(partial.gross_total) }}
              ({{ $t('invoices.closing.paid') }} {{ formatDate(partial.date_of_payment, 'short2') }})
            </label>
          </div>
        </fieldset>
        <fieldset v-if="options.additional_billing_periods.length" class="mt-3" :disabled="busy">
          <legend class="fs-5">{{ $t('invoices.closing.additional') }}</legend>
          <div v-for="period in options.additional_billing_periods" :key="period.id" class="form-check mb-2">
            <input :id="`closing-period-${period.id}`" v-model="additionalBillingPeriodIds" :value="period.id"
                   type="checkbox" class="form-check-input" @change="invalidate">
            <label :for="`closing-period-${period.id}`" class="form-check-label">
              {{ period.label }} · {{ formatDate(period.start_on, 'short2') }}–{{ formatDate(period.end_on, 'short2') }} ·
              {{ formatCurrency(period.price) }} {{ $t('invoices.closing.net') }}
            </label>
          </div>
        </fieldset>
        <button type="button" class="btn btn-outline-primary mt-3" :disabled="busy || !partialInvoiceIds.length" @click="calculate">
          {{ $t('invoices.closing.calculate') }}
        </button>
      </template>
      <template v-if="preview">
        <table class="table mt-3">
          <caption>{{ $t('invoices.closing.proposal') }}</caption>
          <thead>
            <tr><th>{{ $t('invoices.content') }}</th><th class="text-end">{{ $t('invoices.net_total') }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="(position, index) in preview.positions" :key="index">
              <td class="position-text">{{ plainText(position.content) }}</td>
              <td class="text-end text-nowrap">{{ formatCurrency(Number(position.price) * Number(position.count)) }}</td>
            </tr>
          </tbody>
        </table>
        <!-- Installments are deducted as net positions above, so the gross total is the payable amount. -->
        <dl class="row">
          <dt class="col-8">{{ $t('invoices.net_total') }}</dt>
          <dd class="col-4 text-end">{{ formatCurrency(preview.net_total) }}</dd>
          <dt class="col-8">{{ $t('invoices.balance_due') }}</dt>
          <dd class="col-4 text-end fw-bold">{{ formatCurrency(preview.gross_total) }}</dd>
        </dl>
      </template>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary" :disabled="busy" @click="visible = false">{{ $t('invoices.closing.cancel') }}</button>
        <button type="button" class="btn btn-primary" :disabled="busy || !preview" @click="convert">{{ $t('invoices.closing.apply') }}</button>
      </template>
    </Dialog>
  </div>
</template>

<script>
import Dialog from 'primevue/dialog';
import {useInvoiceStore} from '@/stores/invoice';

export default {
  components: {Dialog},
  props: {
    invoice: {type: Object, required: true},
    disabled: {type: Boolean, default: false},
  },
  emits: ['converted'],
  setup() {
    return {invoiceStore: useInvoiceStore()}
  },
  data() {
    return {visible: false, busy: false, options: null, preview: null, error: '', partialInvoiceIds: [], additionalBillingPeriodIds: []}
  },
  methods: {
    selection() {
      return {partial_invoice_ids: this.partialInvoiceIds, additional_billing_period_ids: this.additionalBillingPeriodIds}
    },
    invalidate() {
      this.preview = null
      this.error = ''
    },
    async open() {
      if (this.disabled || this.busy) return
      this.visible = true
      this.busy = true
      this.options = null
      this.invalidate()
      try {
        const {data} = await this.invoiceStore.closingInvoiceOptions(this.invoice.id)
        this.options = data
        this.partialInvoiceIds = data.partial_invoices.map(partial => partial.id)
        this.additionalBillingPeriodIds = []
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('invoices.closing.failed')
      } finally {
        this.busy = false
      }
      if (this.options && this.partialInvoiceIds.length) await this.calculate()
    },
    async calculate() {
      if (this.busy) return
      this.busy = true
      this.invalidate()
      try {
        const {data} = await this.invoiceStore.closingInvoicePreview(this.invoice.id, this.selection())
        this.preview = data
      } catch (error) {
        this.error = error.response?.data?.message || this.$t('invoices.closing.failed')
      } finally {
        this.busy = false
      }
    },
    async convert() {
      if (this.busy || !this.preview) return
      this.busy = true
      this.error = ''
      try {
        await this.invoiceStore.convertToClosingInvoice(this.invoice.id, {...this.selection(), revision: this.preview.revision})
        this.visible = false
        this.$emit('converted')
      } catch (error) {
        this.preview = null
        this.error = error.response?.data?.message || this.$t('invoices.closing.failed')
      } finally {
        this.busy = false
      }
    },
    plainText(content) {
      const document = new DOMParser().parseFromString(content.replace(/<br\s*\/?>|<\/p>/gi, '\n'), 'text/html')
      return document.body.textContent
    },
  },
}
</script>

<style scoped>
.position-text { white-space: pre-line; }
</style>
